import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Circle, Clock, Eye, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { Spinner } from "@/components/Gate";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { FORMAT_LABELS, errMsg, money, useApi } from "@/lib/api";
import { useMe } from "@/context/MeContext";

export default function CourseDetail() {
  const { id } = useParams();
  const api = useApi();
  const navigate = useNavigate();
  const { me } = useMe();
  const [c, setC] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [promo, setPromo] = useState("");

  const load = useCallback(() => api.get(`/courses/${id}`).then((r) => setC(r.data)).catch((e) => setErr(errMsg(e))), [api, id]);
  useEffect(() => { load(); }, [load]);

  const enrol = async () => {
    setBusy(true);
    try {
      if (c.price) {
        const r = await api.post("/payments/checkout", { course_id: id, promo_code: promo, origin_url: window.location.origin });
        window.location.href = r.data.checkout_url;
        return;
      }
      await api.post(`/courses/${id}/enrol`);
      toast.success("You're enrolled!");
      await load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (err) return <p className="text-rose-600" data-testid="course-error">{err}</p>;
  if (!c) return <Spinner />;
  const done = new Set(c.progress?.completed_ids || []);
  const enrolled = !!c.enrolment;
  const firstLesson = c.outline[0]?.lessons[0];
  const resumeId = c.enrolment?.last_lesson_id || c.progress?.next_lesson?.id || firstLesson?.id;
  const isLearner = me.user.role === "learner";

  return (
    <div>
      <Link to="/learn/courses" className="mb-6 inline-flex items-center gap-1 text-sm text-slate-600 hover:text-[#222d99]" data-testid="back-to-library"><ArrowLeft className="h-4 w-4" /> Course library</Link>
      <div className="grid gap-8 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <div className="mb-4 flex flex-wrap gap-2">
            <Badge variant="outline" className="capitalize">{c.level}</Badge>
            {c.skills.map((s) => <Badge key={s} variant="secondary">{s}</Badge>)}
          </div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl" data-testid="course-title">{c.title}</h1>
          <p className="mt-4 text-base leading-relaxed text-slate-600">{c.description}</p>
          {c.prerequisites?.length > 0 && <p className="mt-4 text-sm text-slate-600"><strong>Prerequisites:</strong> {c.prerequisites.join(", ")}</p>}

          <h2 className="mb-4 mt-10 text-xl font-bold text-slate-900">Syllabus</h2>
          <div className="space-y-4" data-testid="course-syllabus">
            {c.outline.map((m, mi) => (
              <div key={m.id} className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                <div className="border-b border-slate-100 bg-slate-50 px-5 py-3">
                  <p className="font-mono-label text-[10px] uppercase tracking-[0.2em] text-slate-500">Module {mi + 1}</p>
                  <p className="font-semibold text-slate-900">{m.title}</p>
                </div>
                <ul>
                  {m.lessons.map((l) => {
                    const open = enrolled || l.is_preview;
                    const Icon = done.has(l.id) ? CheckCircle2 : open ? Circle : Lock;
                    const inner = (
                      <>
                        <Icon className={`h-4 w-4 shrink-0 ${done.has(l.id) ? "text-emerald-600" : "text-slate-400"}`} />
                        <span className="flex-1 text-sm text-slate-800">{l.title}</span>
                        {!enrolled && l.is_preview && <Badge variant="outline" className="gap-1"><Eye className="h-3 w-3" /> Preview</Badge>}
                        <span className="hidden text-xs text-slate-500 sm:inline">{FORMAT_LABELS[l.type]} · {l.duration_min}m</span>
                      </>
                    );
                    return (
                      <li key={l.id} className="border-b border-slate-100 last:border-0">
                        {open ? <Link to={`/learn/lessons/${l.id}`} className="flex items-center gap-3 px-5 py-3 hover:bg-slate-50" data-testid={`syllabus-lesson-${l.id}`}>{inner}</Link>
                          : <div className="flex items-center gap-3 px-5 py-3 opacity-70" data-testid={`syllabus-lesson-locked-${l.id}`}>{inner}</div>}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </div>
        <aside className="lg:sticky lg:top-6 lg:self-start">
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            {c.cover_url && <img src={c.cover_url} alt="" loading="lazy" className="mb-5 aspect-video w-full rounded-xl object-cover" />}
            <p className="font-display text-3xl font-extrabold text-slate-900" data-testid="course-price">{money(c.price, c.currency)}</p>
            <div className="mt-3 flex gap-4 text-sm text-slate-600">
              <span className="flex items-center gap-1"><Clock className="h-4 w-4" /> {c.duration_hours} hours</span>
              <span>{c.lesson_count} lessons</span>
            </div>
            {enrolled ? (
              <>
                <div className="mt-6 flex justify-between text-sm"><span>Your progress</span><span data-testid="course-progress-percent">{c.progress.percent}%</span></div>
                <Progress value={c.progress.percent} className="mt-2 h-2" />
                {c.enrolment.completed_at && <p className="mt-3 text-sm font-semibold text-emerald-700" data-testid="course-completed-label">Course completed</p>}
                <Button onClick={() => navigate(`/learn/lessons/${resumeId}`)} className="mt-6 w-full rounded-full bg-[#222d99] hover:bg-[#1a237e]" data-testid="course-resume-btn">
                  {c.progress.percent ? "Resume" : "Start course"}
                </Button>
              </>
            ) : isLearner ? (
              <>
                <Button onClick={enrol} disabled={busy} className="mt-6 w-full rounded-full bg-[#222d99] hover:bg-[#1a237e]" data-testid="course-enrol-btn">
                  {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {c.price ? "Buy course" : "Enrol for free"}
                </Button>
                {c.price > 0 && <input value={promo} onChange={(e) => setPromo(e.target.value)} placeholder="Promo code (optional)" aria-label="Promo code" className="mt-3 h-10 w-full rounded-md border border-slate-300 px-3 text-sm" data-testid="course-promo-input" />}
                {c.price > 0 && <p className="mt-3 text-xs text-slate-500">Access unlocks once the payment provider confirms your payment.</p>}
              </>
            ) : <p className="mt-6 text-sm text-slate-500">Admin preview — all lessons are open to you.</p>}
          </div>
        </aside>
      </div>
    </div>
  );
}
